import {useEffect, useRef, useCallback, useMemo} from "@wordpress/element";
import {useElements} from "@stripe/react-stripe-js";
import {isEmail, isPhoneNumber} from '@wordpress/url';
import {doShippingAddressUpdate, doShippingRateChange} from '../api-requests';
import {getPage, getSelectedShippingOption, toCartAddress, getShippingOptions} from "../util";

export const useExpressCheckout = (
    {
        total,
        events,
        onClick,
        lineItems,
        shippingData,
        paymentMethod,
        activePaymentMethod
    }) => {
    const elements = useElements();

    const {
        addSuccessResponseData,
        addCreatePaymentMethodData
    } = events;

    const currentData = useRef({total, lineItems, shippingData, elements, response: {}});

    useEffect(() => {
        currentData.current = {
            ...currentData.current,
            total,
            elements,
            lineItems,
            shippingData,
        }
    }, [total, elements, lineItems, shippingData]);

    const onButtonClick = useCallback((event) => {
        const {
            total,
            lineItems,
            shippingData
        } = currentData.current;
        const {
            needsShipping,
            shippingRates
        } = shippingData;
        const data = {};

        const calculatedTotal = lineItems.reduce((total, item) => total + item.amount, 0);

        if (calculatedTotal === total) {
            data.lineItems = lineItems;
        }

        if (needsShipping) {
            const options = getShippingOptions(shippingRates, {label: 'displayName'});
            if (options.length) {
                data.shippingRates = options;
            }
        }

        onClick();

        event.resolve(data);

    }, [onClick]);

    const onConfirm = useCallback((event) => {
        const {
            billingDetails,
            shippingAddress
        } = event;
        if (shippingAddress) {
            const {
                name,
                address
            } = shippingAddress;
            currentData.current.response.shippingAddress = toCartAddress()({...address, name});
        }
        if (billingDetails) {
            const {
                name,
                email = '',
                phone = '',
                address
            } = billingDetails;
            currentData.current.response.billingAddress = toCartAddress()({...address, name});
            if (typeof email === 'string' && isEmail(email)) {
                currentData.current.response.billingAddress.email = email;
            }
            if (typeof phone === 'string' && isPhoneNumber(phone)) {
                currentData.current.response.billingAddress.phone = phone;
            }

        }
    }, []);

    const onShippingAddressChange = useCallback(async (event) => {
        const {
            elements,
            shippingData
        } = currentData.current;
        const {
            shippingAddress
        } = shippingData;
        const {
            reject,
            resolve,
            address
        } = event;

        const intermediateAddress = toCartAddress()(address);

        try {
            const response = await doShippingAddressUpdate({
                address: intermediateAddress,
                payment_method: paymentMethod,
                page_id: getPage()
            });
            if (response.code) {
                throw new Error(response.code);
            }
            shippingData.setShippingAddress({...shippingAddress, ...intermediateAddress});
            const {
                total,
                displayItems,
                shippingOptions
            } = response.data.newData;

            elements.update({
                amount: total.amount
            });
            resolve({
                shippingRates: shippingOptions,
                ...(displayItems.reduce((total, item) => total + item.amount, 0) === total.amount && {
                    lineItems: displayItems
                })
            });
        } catch (error) {
            reject(error);
        }

    }, [
        paymentMethod
    ]);

    const onShippingRateChange = useCallback(async (event) => {
        const {
            elements,
            shippingData
        } = currentData.current;
        const {
            reject,
            resolve,
            shippingRate
        } = event;
        try {
            const response = await doShippingRateChange({
                shipping_method: shippingRate.id,
                payment_method: paymentMethod,
                page_id: getPage()
            });
            if (response.code) {
                throw new Error(response.code);
            }
            shippingData.setSelectedRates(...getSelectedShippingOption(shippingRate.id));
            const {
                total,
                displayItems,
                shippingOptions
            } = response.data.newData;

            elements.update({
                amount: total.amount
            });
            resolve({
                shippingRates: shippingOptions,
                ...(displayItems.reduce((total, item) => total + item.amount, 0) === total.amount && {
                    lineItems: displayItems
                })
            });
        } catch (error) {
            reject(error);
        }
    }, [
        paymentMethod
    ]);

    useEffect(() => {
        if (paymentMethod === activePaymentMethod) {
            const unsubscribe = addCreatePaymentMethodData((data) => {
                delete data.params;
                return data;
            });

            return unsubscribe;
        }
    }, [
        paymentMethod,
        activePaymentMethod,
        addCreatePaymentMethodData
    ]);

    useEffect(() => {
        if (paymentMethod === activePaymentMethod) {
            const unsubscribe = addSuccessResponseData((data, {billingAddress, shippingAddress}) => {
                const {response} = currentData.current;
                if (response.billingAddress) {
                    data.meta.billingAddress = {
                        ...billingAddress,
                        ...response.billingAddress
                    }
                }
                if (response.shippingAddress) {
                    data.meta.shippingAddress = {
                        ...shippingAddress,
                        ...response.shippingAddress
                    }
                }
                return data;
            });

            return unsubscribe;
        }
    }, [
        paymentMethod,
        activePaymentMethod,
        addSuccessResponseData
    ]);


    return {
        onConfirm,
        onButtonClick,
        onShippingRateChange,
        onShippingAddressChange
    }
}